import React, { useState, useEffect, useRef } from 'react';
import { ConfirmRemoveBundleDialog } from '@/components/ConfirmRemoveBundleDialog';
import { ReturnCheckDialog } from '@/components/processingJo/ReturnCheckDialog';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, PackageCheck, Scan, Check, X, AlertTriangle, RefreshCw, Loader2, Truck, Search, Printer, Sparkles, Users, UserCheck, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import PrintDialog from '@/components/PrintDialog';
import { useOperationDraft } from '@/hooks/useOperationDraft';
import { DraftsMenu } from '@/components/DraftsMenu';
import { ResumedDraftBanner } from '@/components/ResumedDraftBanner';

const InhouseBundleReceiptEntry = () => {
  const navigate = useNavigate();
  const bundleInputRef = useRef(null);
  const originalInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [scannedBundles, setScannedBundles] = useState([]);
  const [bundleInput, setBundleInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumedDraft, setResumedDraft] = useState(null);

  // 2026-06 — Select Document: server-side search + client-side pagination so
  // OLDER received documents (previously dropped by a hard 100-row cap) are
  // findable. `docSearchInput` is debounced into a backend `?search=` query.
  const [docSearchInput, setDocSearchInput] = useState('');
  const [docSearching, setDocSearching] = useState(false);
  const [docPage, setDocPage] = useState(1);
  const DOC_PAGE_SIZE = 25;
  const docSearchTimer = useRef(null);
  const docReqSeq = useRef(0);
  const docSearchInputRef = useRef(null);

  // Original bundle dialog
  const [showOriginalDialog, setShowOriginalDialog] = useState(false);
  const [currentGateBundle, setCurrentGateBundle] = useState('');
  const [originalBundleCode, setOriginalBundleCode] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [greigData, setGreigData] = useState(null);
  const [returnCheck, setReturnCheck] = useState(null);
  const [needsManual, setNeedsManual] = useState(false);

  // Manual entry
  const [manualSortNo, setManualSortNo] = useState('');
  const [manualColor, setManualColor] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [manualVendor, setManualVendor] = useState('');
  // 2026-06 — Combined UI: operator-entered Received Qty for the merged
  // Processing Inward step (defaults to the expected/sent qty; type=text with
  // numeric sanitation so barcode-scanner / tablet keyboards can edit it).
  const [receivedQty, setReceivedQty] = useState('');
  const [lookingUpSort, setLookingUpSort] = useState(false);
  const [colorOptions, setColorOptions] = useState([]);
  const [colorSearch, setColorSearch] = useState('');
  const [showColorDropdown, setShowColorDropdown] = useState(false);

  // Admin: edit saved bundle qty
  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const isAdmin = currentUser?.role === 'Admin';

  // Phase A — Inhouse 4-flow foundation.
  // 2026-06: flow-type selection is available to ALL users (was
  // Supervisor/Incharge/Admin only). Backend write paths were relaxed to match.
  const FLOW_TYPE_OPTIONS = [
    { value: 'REGULAR', label: 'Regular' },
    { value: 'FLEXI', label: 'Flexi (skip Pressing)' },
    { value: 'SHIRTING', label: 'Shirting (Mtr → pcs)' },
    { value: 'RUNNING_MTR', label: 'Running Mtr (1 USN/bundle)' },
    { value: 'THAN_PROCESSING', label: 'Than Processing (skip to Internal Transfer)' },
    { value: 'JOB_WORKER', label: 'Job Worker (send out — skip Folding & Pressing)' },
  ];
  const canPickFlowType = true;
  const [flowType, setFlowType] = useState('REGULAR');
  const [consumptionPerPc, setConsumptionPerPc] = useState('');
  const [receivedMtr, setReceivedMtr] = useState('');
  // 2026-06 — JOB_WORKER flow: pick the job worker (vendor) the bundle is
  // being sent out to for folding & pressing. Sticky across bundles in a
  // session so the operator doesn't re-pick for every scan.
  const [jobWorkers, setJobWorkers] = useState([]);
  const [jobWorkerName, setJobWorkerName] = useState('');

  // Print
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [printCodes, setPrintCodes] = useState([]);
  const [printExtraTexts, setPrintExtraTexts] = useState(null);

  const [editQtyOpen, setEditQtyOpen] = useState(false);
  const [editBundleCode, setEditBundleCode] = useState('');
  const [editNewQty, setEditNewQty] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editPreview, setEditPreview] = useState(null);

  // Multi-IT jobworker truck flow — pending Processing → Inhouse transfers.
  // The jobworker truck combines bundles from many PROC ITs into one
  // shipment, so we resolve the IT lazily as each bundle is scanned.
  const [procTransfers, setProcTransfers] = useState([]);
  const [procScanInput, setProcScanInput] = useState('');
  const [procScanning, setProcScanning] = useState(false);
  const [procExpandedIds, setProcExpandedIds] = useState(new Set());
  const procScanInputRef = useRef(null);

  // Recent receipts + valid-bundle pool for original-bundle correction.
  const [recentReceipts, setRecentReceipts] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [validBundlePool, setValidBundlePool] = useState(new Set());

  // 2026-06 — Phase 2 merge: multi-staff session so that each bundle is
  // auto-processed-inward right after receipt+print, crediting these staff
  // (received qty split equally, same as Assortment Packing). Required in
  // the "Select Document" flow before gate bundles can be scanned.
  const [piStaffList, setPiStaffList] = useState([]); // [{staff_id, staff_name}]
  const [piStaffValue, setPiStaffValue] = useState('');
  const [piStaffValidating, setPiStaffValidating] = useState(false);
  const piStaffInputRef = useRef(null);

  // 2026-05-20 — date range selector for Recent Receipts (max 7-day span).
  const _todayStr = () => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 10);
  };
  const _daysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 10);
  };
  const [recentFromDate, setRecentFromDate] = useState(_todayStr());
  const [recentToDate, setRecentToDate] = useState(_todayStr());

  // Discard incomplete draft (per row).
  const [discardingCode, setDiscardingCode] = useState(null);
  const [editOrigOpen, setEditOrigOpen] = useState(false);
  const [editOrigRow, setEditOrigRow] = useState(null); // the receipt row being edited
  const [editOrigNewValue, setEditOrigNewValue] = useState('');
  const [editOrigReason, setEditOrigReason] = useState('');
  const [editOrigBusy, setEditOrigBusy] = useState(false);

  // Phase E follow-up — edit a bundle's flow_type before Folding kicks in.
  const [editFlowOpen, setEditFlowOpen] = useState(false);
  const [editFlowRow, setEditFlowRow] = useState(null);
  const [editFlowNewType, setEditFlowNewType] = useState('REGULAR');
  const [editFlowConsumption, setEditFlowConsumption] = useState('');
  const [editFlowReceivedMtr, setEditFlowReceivedMtr] = useState('');
  const [editFlowReason, setEditFlowReason] = useState('supervisor_correction');
  const [editFlowBusy, setEditFlowBusy] = useState(false);

  const openEditFlowDialog = (row) => {
    setEditFlowRow(row);
    setEditFlowNewType(row?.flow_type || 'REGULAR');
    setEditFlowConsumption(row?.consumption_per_pc != null ? String(row.consumption_per_pc) : '');
    setEditFlowReceivedMtr(row?.received_mtr != null ? String(row.received_mtr) : '');
    setEditFlowReason('supervisor_correction');
    setEditFlowOpen(true);
  };

  const handleSubmitFlowEdit = async () => {
    if (!editFlowRow) return;
    const payload = {
      bundle_code: editFlowRow.bundle_code,
      new_flow_type: editFlowNewType,
      reason: editFlowReason || 'supervisor_correction',
    };
    if (editFlowNewType === 'SHIRTING') {
      const cpp = parseFloat(editFlowConsumption);
      const rm = parseFloat(editFlowReceivedMtr);
      if (!cpp || cpp <= 0) { toast.error('SHIRTING needs consumption/pc > 0'); return; }
      if (!rm || rm <= 0) { toast.error('SHIRTING needs received mtr > 0'); return; }
      payload.consumption_per_pc = cpp;
      payload.received_mtr = rm;
    } else if (editFlowNewType === 'RUNNING_MTR') {
      const rm = parseFloat(editFlowReceivedMtr);
      if (!rm || rm <= 0) { toast.error('RUNNING_MTR needs received mtr > 0'); return; }
      payload.received_mtr = rm;
    }
    setEditFlowBusy(true);
    try {
      const resp = await apiClient.post('/inhouse/bundle-receipt/edit-flow-type', payload);
      toast.success(resp.data?.message || 'Flow type updated');
      setEditFlowOpen(false);
      // Refresh the recent receipts panel.
      try { await fetchRecentReceipts(); } catch (_) { /* no-op */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update flow type');
    } finally {
      setEditFlowBusy(false);
    }
  };

  const handleEditLookup = async () => {
    const bc = editBundleCode.trim();
    if (!bc) { toast.error('Bundle code required'); return; }
    setEditLoading(true);
    try {
      const resp = await apiClient.get(`/processing-inward/lookup?bundle_code=${encodeURIComponent(bc)}`);
      setEditPreview({
        bundle_code: bc,
        current_qty: resp.data?.bundle?.usn_count ?? resp.data?.usn_count ?? '?',
        sort_no: resp.data?.bundle?.sort_no ?? '',
        design_name: resp.data?.bundle?.design_name ?? '',
        shade: resp.data?.bundle?.shade ?? '',
      });
    } catch (err) {
      setEditPreview(null);
      toast.error(err.response?.data?.detail || 'Bundle not found');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditQtySubmit = async () => {
    const bc = editBundleCode.trim();
    const nq = parseInt(editNewQty, 10);
    if (!bc || isNaN(nq) || nq < 0) { toast.error('Valid bundle code and new qty required'); return; }
    if (!window.confirm(
      `Update ${bc} qty to ${nq}?\n\nThis will claim or release PI USNs accordingly. Release is blocked if USNs have moved downstream.`
    )) return;
    setEditLoading(true);
    try {
      const resp = await apiClient.post('/processing-inward/admin/edit-bundle-qty', {
        bundle_code: bc,
        new_qty: nq,
        reason: editReason.trim(),
      });
      const d = resp.data;
      toast.success(
        `Qty updated ${d.old_qty} → ${d.new_qty}` +
        (d.added_usns?.length ? ` (+${d.added_usns.length} new)` : '') +
        (d.removed_usns?.length ? ` (-${d.removed_usns.length} released)` : '')
      );
      setEditQtyOpen(false);
      setEditBundleCode(''); setEditNewQty(''); setEditReason(''); setEditPreview(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update qty');
    } finally {
      setEditLoading(false);
    }
  };

  const {
    drafts, activeDraftId, saving, lastSavedAt,
    saveNow, clearDraft, loadDraft, removeDraft, setActiveDraftId,
  } = useOperationDraft({ moduleKey: 'section:Inhouse:combined', operationType: 'inhouse_combined' });

  // Combined receipt+PI commits each bundle individually, so we snapshot a
  // recovery draft on EVERY confirm + print click. The draft is cleared only
  // when the operator presses Complete Receipt (handleSubmitReceipt) or deletes
  // it from the Drafts menu.
  const persistCombinedDraft = (nextScanned) => {
    if (!selectedDocument || nextScanned.length === 0) return;
    const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
    saveNow({
      label: `Inhouse — ${docId}`,
      bundleCount: nextScanned.length,
      documentId: docId,
      documentNo: docId,
      payload: { document: selectedDocument, staff: piStaffList, scanned: nextScanned },
    });
  };

  const handleResumeDraft = async (draftId) => {
    try {
      const doc = await loadDraft(draftId);
      const p = doc.payload || {};
      setSelectedDocument(p.document || null);
      setPiStaffList(p.staff || []);
      setScannedBundles(p.scanned || []);
      setBundleInput('');
      const summary = (drafts || []).find((x) => x.draft_id === draftId) || null;
      setResumedDraft(summary);
      toast.success(summary?.last_updated_by_name ? `Resumed draft — last worked on by ${summary.last_updated_by_name}` : 'Draft restored');
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch {
      toast.error('Failed to restore draft');
    }
  };

  useEffect(() => { fetchDocuments(); fetchProcTransfers(); fetchRecentReceipts(); fetchValidBundlePool(); }, []);
  useEffect(() => { if (selectedDocument && bundleInputRef.current) bundleInputRef.current.focus(); }, [selectedDocument]);
  useEffect(() => {
    apiClient.get('/processing-inward/colors').then(r => setColorOptions(r.data.colors || [])).catch(() => {});
    apiClient.get('/processing-inward/job-workers').then(r => setJobWorkers(r.data.job_workers || [])).catch(() => {});
  }, []);

  const fetchProcTransfers = async () => {
    try {
      const r = await apiClient.get('/inhouse/bundle-receipt/pending-from-processing');
      setProcTransfers(r.data.transfers || []);
    } catch {
      // Non-fatal — just log; main fetch flow still works.
    }
  };

  const fetchRecentReceipts = async (fromOverride, toOverride) => {
    setRecentLoading(true);
    try {
      const fromD = fromOverride ?? recentFromDate;
      const toD = toOverride ?? recentToDate;
      // Validate 7-day cap client-side too (UX guard; backend is authoritative).
      const fromTs = new Date(fromD + 'T00:00:00').getTime();
      const toTs = new Date(toD + 'T23:59:59').getTime();
      if (!Number.isFinite(fromTs) || !Number.isFinite(toTs)) {
        toast.error('Invalid date');
        return;
      }
      if (fromTs > toTs) {
        toast.error('"From" date must be on or before "To" date');
        return;
      }
      if (toTs - fromTs > 7 * 24 * 60 * 60 * 1000) {
        toast.error('Date range cannot exceed 7 days');
        return;
      }
      const fromIso = new Date(fromD + 'T00:00:00').toISOString();
      const toIso = new Date(toD + 'T23:59:59').toISOString();
      const r = await apiClient.get(
        `/inhouse/bundle-receipt/recent?from_dt=${encodeURIComponent(fromIso)}&to_dt=${encodeURIComponent(toIso)}`,
      );
      setRecentReceipts(r.data.receipts || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load recent receipts');
    } finally {
      setRecentLoading(false);
    }
  };

  const handleDiscardReceipt = async (row) => {
    if (!row?.bundle_code) return;
    if (!window.confirm(
      `Discard this incomplete draft?\n\n` +
      `Bundle: ${row.bundle_code}\n` +
      `Original: ${row.original_bundle || '-'}\n` +
      `Qty: ${row.usn_count ?? 0}\n\n` +
      `This will:\n` +
      `  • Release ${row.usn_count ?? 0} PI USN(s) back to the pool\n` +
      `  • Un-mark the bundle from its Processing IT (re-opens IT if needed)\n` +
      `  • Delete the receipt row\n\n` +
      `Use this only for half-finished entries (e.g. system crashed before Submit).`
    )) return;
    setDiscardingCode(row.bundle_code);
    try {
      const resp = await apiClient.post('/inhouse/bundle-receipt/discard-receipt', {
        bundle_code: row.bundle_code,
        reason: 'incomplete_draft_cleanup_by_user',
      });
      toast.success(resp.data?.message || 'Receipt discarded');
      await Promise.all([
        fetchRecentReceipts(),
        fetchValidBundlePool(),
        fetchProcTransfers(),
      ]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to discard receipt');
    } finally {
      setDiscardingCode(null);
    }
  };

  const fetchValidBundlePool = async () => {
    try {
      const r = await apiClient.get('/inhouse/bundle-receipt/valid-original-bundles');
      setValidBundlePool(new Set(r.data.bundle_codes || []));
    } catch {
      // Non-fatal — backend will enforce anyway.
    }
  };

  const openEditOriginalDialog = (row) => {
    setEditOrigRow(row);
    setEditOrigNewValue('');
    setEditOrigReason('');
    setEditOrigOpen(true);
  };

  const handleSubmitOriginalEdit = async () => {
    const nv = (editOrigNewValue || '').trim();
    if (!nv) { toast.error('New original bundle number is required'); return; }
    // 2026-05-04: hard-block against the live PROC pool was rolled back.
    // Legacy bundles are still flowing into Inhouse and must be allowed.
    setEditOrigBusy(true);
    try {
      const r = await apiClient.post('/inhouse/bundle-receipt/edit-original-bundle', {
        bundle_code: editOrigRow.bundle_code,
        new_original_bundle: nv,
        reason: (editOrigReason || 'typo_correction').trim(),
      });
      toast.success(r.data.message || 'Original bundle corrected');
      setEditOrigOpen(false);
      await Promise.all([fetchRecentReceipts(), fetchValidBundlePool(), fetchProcTransfers()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Edit failed');
    } finally {
      setEditOrigBusy(false);
    }
  };

  const handleScanProcBundle = async () => {
    const code = procScanInput.trim();
    if (!code) return;
    setProcScanning(true);
    try {
      const res = await apiClient.post('/inhouse/bundle-receipt/scan-original-bundle', { bundle_code: code });
      const d = res.data;
      toast.success(
        d.all_complete
          ? `${code} received — IT ${d.transfer_id} fully closed`
          : `${code} received → ${d.transfer_id} (${d.received_count}/${d.total_bundles})`
      );
      setProcScanInput('');
      // Auto-expand the IT card the bundle landed in
      setProcExpandedIds((prev) => new Set([...prev, d.transfer_id]));
      await fetchProcTransfers();
      setTimeout(() => procScanInputRef.current?.focus(), 50);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Scan failed';
      toast.error(detail);
    } finally {
      setProcScanning(false);
    }
  };

  const toggleProcExpanded = (tid) => {
    setProcExpandedIds((prev) => {
      const n = new Set(prev);
      n.has(tid) ? n.delete(tid) : n.add(tid);
      return n;
    });
  };

  const fetchDocuments = async (searchOverride, opts = {}) => {
    const silent = !!opts.silent;
    const seq = ++docReqSeq.current;
    if (silent) setDocSearching(true); else setIsLoading(true);
    try {
      const term = (searchOverride !== undefined ? searchOverride : docSearchInput).trim();
      const params = new URLSearchParams();
      if (term) params.set('search', term);
      params.set('limit', '1000');
      const response = await apiClient.get(
        `/consignments/section/${encodeURIComponent('Inhouse')}/incoming?${params.toString()}`,
      );
      // Stale-response guard: only the latest-issued request may update state
      // (a slow initial load must not clobber a newer search result).
      if (seq !== docReqSeq.current) return;
      // Filter rules for "Select Document":
      //  1. REWORK / rework_skip_intermediate — handled via Assortment Packing fast-path,
      //     must NOT appear here.
      //  2. Processing-source ITs — handled by the top "Pending Processing Transfers"
      //     panel (multi-IT jobworker truck flow), must NOT be duplicated here either.
      const docsForReceipt = (response.data.consignments || []).filter(c => {
        if (c.status !== 'received') return false;
        if (c.rework_skip_intermediate) return false;
        if ((c.transfer_type || '').toUpperCase() === 'REWORK') return false;
        const from = (c.from_section || '').toLowerCase();
        if (from.startsWith('processing')) return false;
        return true;
      });
      setDocuments(docsForReceipt);
      setDocPage(1);
    } catch (err) {
      if (seq === docReqSeq.current) {
        console.error('fetchDocuments failed', err);
        toast.error('Failed to load documents');
      }
    }
    finally { if (seq === docReqSeq.current) { if (silent) setDocSearching(false); else setIsLoading(false); } }
  };

  const handleDocSearchChange = (val) => {
    setDocSearchInput(val);
    if (docSearchTimer.current) clearTimeout(docSearchTimer.current);
    docSearchTimer.current = setTimeout(() => {
      fetchDocuments(val, { silent: true });
    }, 400);
  };

  const handleDocSearchSubmit = () => {
    if (docSearchTimer.current) clearTimeout(docSearchTimer.current);
    fetchDocuments(docSearchInput, { silent: true });
  };

  const handleSelectDocument = (docId) => {
    const existing = (drafts || []).find((d) => d.document_id === docId);
    if (existing) { handleResumeDraft(existing.draft_id); return; }
    const doc = documents.find(d => (d.transfer_id || d.entry_id) === docId);
    setResumedDraft(null);
    setSelectedDocument(doc);
    setScannedBundles([]);
    setBundleInput('');
    setActiveDraftId(null);
  };

  const handleStartFresh = async () => {
    const id = resumedDraft?.draft_id;
    if (id) {
      const ok = await removeDraft(id);
      if (!ok) { toast.error('Could not discard the shared draft — please try again'); return; }
    }
    setResumedDraft(null);
    setSelectedDocument(null);
    setScannedBundles([]);
    setPiStaffList([]);
    setBundleInput('');
  };

  // Step 1: Scan gate entry bundle barcode
  const handleScanGateBundle = () => {
    if (piStaffList.length === 0) {
      toast.error('Add at least one staff before scanning bundles');
      setTimeout(() => piStaffInputRef.current?.focus(), 60);
      return;
    }
    const barcode = bundleInput.trim().toUpperCase();
    if (!barcode) return;
    if (scannedBundles.some(b => b.gate_bundle === barcode)) {
      toast.error('Bundle already scanned'); setBundleInput(''); return;
    }
    // Validate against document bundles
    const docBundles = selectedDocument.bundles || selectedDocument.bundle_codes || [];
    let bundleBarcodes = [];
    if (docBundles.length > 0) {
      bundleBarcodes = typeof docBundles[0] === 'string' ? docBundles : docBundles.map(b => b.bundle_barcode || b);
    }
    if (bundleBarcodes.length > 0 && !bundleBarcodes.includes(barcode)) {
      toast.error(`Bundle ${barcode} is not part of this document`); setBundleInput(''); return;
    }

    // Open dialog for original bundle number
    setCurrentGateBundle(barcode);
    setOriginalBundleCode('');
    setGreigData(null);
    setNeedsManual(false);
    setManualSortNo(''); setManualColor(''); setManualQty(''); setManualVendor('');
    setReceivedQty('');
    setFlowType('REGULAR');
    setConsumptionPerPc('');
    setReceivedMtr('');
    setShowOriginalDialog(true);
    setBundleInput('');
    setTimeout(() => originalInputRef.current?.focus(), 200);
  };

  // Step 2: Look up original bundle in greige collections
  const handleLookupOriginal = async () => {
    const code = originalBundleCode.trim();
    if (!code) return;
    setLookingUp(true);
    setGreigData(null);
    setNeedsManual(false);
    try {
      const res = await apiClient.get(`/processing-inward/lookup-original-bundle?bundle_code=${encodeURIComponent(code)}`);
      if (res.data.has_greige_data) {
        setGreigData(res.data);
        setNeedsManual(false);
        // Default Received Qty to the expected/sent qty — operator can adjust
        // it if the physical count differs (short / excess).
        const expected = res.data.sent_qty || res.data.usn_count || 0;
        setReceivedQty(expected ? String(expected) : '');
      } else {
        setGreigData(res.data);
        setNeedsManual(true);
      }
      // 2026-06 — carry RUNNING_MTR meters from the Greige section: pre-select
      // the RUNNING_MTR flow and pre-fill the received metres (operator can edit).
      if (res.data.suggested_flow_type === 'RUNNING_MTR') {
        setFlowType('RUNNING_MTR');
        if (res.data.suggested_received_mtr) {
          setReceivedMtr(String(res.data.suggested_received_mtr));
          toast.info(`Running Mtr bundle — ${res.data.suggested_received_mtr} m carried from Greige`);
        } else {
          toast.info('Running Mtr bundle — enter the received metres');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bundle not found');
      setNeedsManual(true);
      setGreigData(null);
    } finally { setLookingUp(false); }
  };

  // Sort No -> Vendor auto-fetch
  const handleSortNoLookup = async () => {
    if (!manualSortNo.trim()) return;
    setLookingUpSort(true);
    try {
      const res = await apiClient.get(`/processing-inward/lookup-sort-no?sort_no=${encodeURIComponent(manualSortNo.trim())}`);
      setManualVendor(res.data.vendor_name || '');
      toast.success(`Vendor: ${res.data.vendor_name}`);
    } catch { toast.error('Sort No not found'); setManualVendor(''); }
    finally { setLookingUpSort(false); }
  };

  // Step 3: Confirm and add to list
  const handleConfirmOriginal = async () => {
    const origCode = originalBundleCode.trim();
    if (!origCode) { toast.error('Enter original bundle number'); return; }

    // 2026-06 — JOB_WORKER flow requires a job worker to be selected before
    // the bundle can be received (it will be dispatched out to them).
    if (canPickFlowType && flowType === 'JOB_WORKER' && !jobWorkerName.trim()) {
      toast.error('Select a Job Worker for the Job Worker flow');
      return;
    }

    // Duplicate check — gate bundle or original bundle already in list (current session)
    if (scannedBundles.some(b => b.gate_bundle === currentGateBundle || b.bundle_barcode === origCode)) {
      toast.error('This bundle is already scanned'); setShowOriginalDialog(false); setBundleInput(''); return;
    }

    // 2026-05-18: Hard global duplicate check — once received in
    // inhouse_bundle_receipts (any document, any date), the original
    // bundle is forever-locked. Pre-check BEFORE allocating PI USNs so
    // a doomed save never wastes a PI pool slot.
    try {
      const dupResp = await apiClient.get(
        `/processing-inward/check-original-bundle-duplicate?original_bundle=${encodeURIComponent(origCode)}`,
      );
      if (dupResp.data?.is_duplicate) {
        const ex = dupResp.data.existing || {};
        const when = (ex.created_at || '').slice(0, 10) || 'earlier';
        const who = ex.created_by_name ? ` by ${ex.created_by_name}` : '';
        const gateHint = ex.gate_bundle ? ` (gate barcode: ${ex.gate_bundle})` : '';
        toast.error(
          `Original bundle ${origCode} was already received on ${when}${who}${gateHint}. Duplicate not allowed.`,
          { duration: 10000 },
        );
        return;
      }
    } catch (e) {
      // If the check endpoint itself errors, fall through — backend
      // save_bundle_receipt has the authoritative gate and will still
      // reject the duplicate with a 409.
      console.warn('Duplicate pre-check failed; relying on backend gate', e);
    }

    let sortNo = '', color = '', qty = 0, vendor = '', designName = '';
    let isLegacy = false;
    if (greigData && !needsManual) {
      sortNo = greigData.sort_no || '';
      color = greigData.color || greigData.shade || '';
      qty = greigData.sent_qty || greigData.usn_count || 0;
      vendor = greigData.vendor_name || '';
      designName = greigData.design_name || '';
    } else {
      if (!manualSortNo.trim()) { toast.error('Sort No required'); return; }
      if (!manualColor.trim()) { toast.error('Color required'); return; }
      sortNo = manualSortNo.trim();
      color = manualColor.trim();
      vendor = manualVendor.trim();
      isLegacy = true;

      // 2026-06-16 — SHIRTING legacy bundles derive their piece count from
      // (received_mtr ÷ consumption_per_pc), so the operator-typed Qty
      // field is ignored downstream by the backend SHIRTING branch. To
      // avoid an orphan /allocate-pi-usns claim that strands USNs in the
      // pool, compute the same value here and use it for allocation +
      // hard-block obviously bad input (e.g., cpp >= received_mtr, which
      // is the production bug that produced "only 1 piece received").
      if (canPickFlowType && flowType === 'SHIRTING') {
        const cpp = parseFloat(consumptionPerPc);
        const rm = parseFloat(receivedMtr);
        if (!cpp || cpp <= 0) {
          toast.error('SHIRTING requires Consumption / Pc > 0');
          return;
        }
        if (!rm || rm <= 0) {
          toast.error('SHIRTING requires Received Mtr > 0');
          return;
        }
        qty = Math.floor(rm / cpp);
        if (qty <= 1) {
          toast.error(
            `Configured values yield only ${qty} piece(s) (${rm} Mtr ÷ ${cpp} Mtr/pc). ` +
            `Consumption / Pc must be the metres for ONE piece (typically 1.0–3.0 Mtr).`,
            { duration: 10000 },
          );
          return;
        }
      } else if (canPickFlowType && flowType === 'RUNNING_MTR') {
        // RUNNING_MTR always mints exactly 1 USN per bundle.
        qty = 1;
      } else {
        if (!manualQty || parseInt(manualQty) <= 0) { toast.error('Qty required'); return; }
        qty = parseInt(manualQty);
      }
    }

    // Combined UI (Phase 2 merge) — resolve the operator-entered Received Qty
    // for the auto Processing Inward. Piece flows use the typed value (defaults
    // to the expected/sent qty); Mtr-based flows keep their own metre value.
    const effFlowForPI = canPickFlowType ? flowType : 'REGULAR';
    let receivedForPI;
    if (effFlowForPI === 'RUNNING_MTR') {
      receivedForPI = parseFloat(receivedMtr) || 0;
    } else if (effFlowForPI === 'SHIRTING') {
      receivedForPI = qty;
    } else if (receivedQty.trim() === '') {
      receivedForPI = qty;
    } else {
      const rq = parseInt(receivedQty, 10);
      if (Number.isNaN(rq) || rq <= 0) {
        toast.error('Enter a valid Received Qty'); return;
      }
      receivedForPI = rq;
    }

    // Legacy bundles: pre-allocate PI USNs ONLY for THAN_PROCESSING. Regular/Flexi/
    // Job Worker bind pool stickers later; Shirting/Running Mtr are claimed server-side.
    let allocatedUsns = [];
    if (isLegacy && qty > 0 && effFlowForPI === 'THAN_PROCESSING') {
      try {
        const allocResp = await apiClient.post('/processing-inward/allocate-pi-usns', {
          bundle_code: origCode,
          quantity: qty,
          sort_no: sortNo,
          vendor_name: vendor,
          color: color,
        });
        allocatedUsns = allocResp.data.usn_codes || [];
        toast.success(`${allocatedUsns.length} PI USNs allocated`);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to allocate PI USNs. Cannot proceed.');
        return; // Do NOT proceed without USN allocation
      }
    }

    // Save bundle receipt to inhouse_bundle_receipts for ALL bundles
    try {
      await apiClient.post('/processing-inward/save-bundle-receipt', {
        bundle_code: origCode,
        original_bundle: origCode,
        gate_bundle: currentGateBundle,
        sort_no: sortNo,
        color: color,
        qty: qty,
        vendor_name: vendor,
        design_name: designName,
        sku_code: greigData?.barcode || '',
        is_legacy: isLegacy,
        usn_codes: allocatedUsns.length > 0 ? allocatedUsns : (greigData?.usn_codes || []),
        flow_type: canPickFlowType ? flowType : 'REGULAR',
        consumption_per_pc: (
          canPickFlowType && flowType === 'SHIRTING' && consumptionPerPc.trim()
            ? parseFloat(consumptionPerPc) : null
        ),
        received_mtr: (
          canPickFlowType && (flowType === 'SHIRTING' || flowType === 'RUNNING_MTR') && receivedMtr.trim()
            ? parseFloat(receivedMtr) : null
        ),
        job_worker_name: (canPickFlowType && flowType === 'JOB_WORKER') ? jobWorkerName.trim() : '',
        received_qty: effFlowForPI === 'RUNNING_MTR' ? null : receivedForPI,
      });
    } catch (err) {
      const det = err.response?.data?.detail;
      if (det?.code === 'PJO_RETURN_CHECK') { setReturnCheck(det); return; }
      // Don't block if save fails for non-legacy (data exists in greige collections)
      if (isLegacy) {
        toast.error(typeof det === 'string' ? det : det?.message || 'Failed to save bundle receipt');
        return;
      }
    }

    // === PHASE 2 MERGE: immediately perform Processing Inward for this
    // bundle, crediting the session staff (received qty split equally). The
    // receipt qty IS the received qty here, so it's always a clean match. ===
    let inwardStatus = 'failed';
    const inwardQty = receivedForPI;
    // The receipt was already saved above. `confirm` is idempotent server-side
    // (a retry returns the committed batch as success), so a transient
    // timeout/network blip must NOT mark a truly-committed bundle "Failed".
    // Try once, and on error retry once before giving up.
    const runConfirm = () => apiClient.post('/processing-inward/confirm', {
      bundle_code: origCode,
      received_qty: inwardQty,
      remarks: '',
      staff_ids: piStaffList.map((s) => s.staff_id),
    });
    try {
      let piResp;
      try {
        piResp = await runConfirm();
      } catch (firstErr) {
        // Retry once — if the first call actually committed (slow/timeout),
        // the idempotent endpoint now returns the committed status as success.
        piResp = await runConfirm();
      }
      inwardStatus = piResp.data.receipt_status || 'confirmed';
      toast.success(`Processing Inward done — ${origCode} (${inwardStatus})`);
    } catch (err) {
      toast.error(`Receipt saved, but Processing Inward failed: ${err.response?.data?.detail || 'error'}`);
    }

    const newRow = {
      gate_bundle: currentGateBundle,
      bundle_barcode: currentGateBundle,
      original_bundle: origCode,
      sort_no: sortNo,
      color: color,
      qty: qty,
      received_qty: receivedForPI,
      vendor_name: vendor,
      design_name: designName,
      is_legacy: isLegacy,
      flow_type: canPickFlowType ? flowType : 'REGULAR',
      consumption_per_pc: (canPickFlowType && flowType === 'SHIRTING' && consumptionPerPc.trim()) ? parseFloat(consumptionPerPc) : null,
      received_mtr: (canPickFlowType && (flowType === 'SHIRTING' || flowType === 'RUNNING_MTR') && receivedMtr.trim()) ? parseFloat(receivedMtr) : null,
      job_worker_name: (canPickFlowType && flowType === 'JOB_WORKER') ? jobWorkerName.trim() : '',
      allocated_usns: allocatedUsns,
      scanned_at: new Date().toISOString(),
      status: 'scanned',
      inward_status: inwardStatus,
    };
    const nextScanned = [...scannedBundles, newRow];
    setScannedBundles(nextScanned);

    setShowOriginalDialog(false);

    // Print sticker — 2 copies (bundle code + sort no + qty).
    // 2026-06 — The sticker must show the RECEIVED qty (what physically
    // arrived / was processed inward), NOT the greige/legacy sent qty.
    const sortLabel = sortNo ? `Sort: ${sortNo}` : '';
    const qtyLabel = effFlowForPI === 'RUNNING_MTR'
      ? `Qty: ${receivedForPI} Mtr`
      : `Qty: ${receivedForPI}`;
    const extraText = [sortLabel, qtyLabel].filter(Boolean).join(' | ');
    setPrintCodes([origCode, origCode]);
    setPrintExtraTexts([extraText, extraText]);
    setShowPrintDialog(true);

    // Special rule: snapshot a recovery draft on every confirm + print.
    persistCombinedDraft(nextScanned);
  };

  const handlePrintClose = () => {
    setShowPrintDialog(false);
    setPrintCodes([]);
    // Also persist on the print action so a crash right after printing recovers.
    persistCombinedDraft(scannedBundles);
    // AUTO-CLOSE: once every expected bundle is SUCCESSFULLY received, finalise
    // automatically. Bundles whose Processing Inward failed do NOT count, so a
    // failed last scan keeps the document open for the operator to retry.
    const committed = scannedBundles.filter((b) => b.inward_status && b.inward_status !== 'failed');
    const expectedTotal = selectedDocument?.bundles?.length
      || selectedDocument?.bundle_codes?.length
      || selectedDocument?.bundle_count
      || 0;
    if (expectedTotal > 0 && committed.length >= expectedTotal) {
      closeReceipt(committed);
      return;
    }
    setTimeout(() => bundleInputRef.current?.focus(), 100);
  };

  const [removeBundleTarget, setRemoveBundleTarget] = useState(null);

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.gate_bundle !== barcode));
  };

  // Phase 2 merge — multi-staff session helpers (auto Processing Inward).
  const handleAddPiStaff = async (e) => {
    e.preventDefault();
    const id = piStaffValue.trim();
    if (!id || piStaffValidating) return;
    if (piStaffList.some((s) => s.staff_id === id)) {
      toast.error('Staff already added'); setPiStaffValue(''); return;
    }
    setPiStaffValidating(true);
    try {
      const resp = await apiClient.post('/processing-inward/validate-staff', { staff_id: id });
      setPiStaffList((prev) => [...prev, { staff_id: resp.data.staff_id, staff_name: resp.data.staff_name }]);
      setPiStaffValue('');
      toast.success(`Added ${resp.data.staff_name}`);
      setTimeout(() => piStaffInputRef.current?.focus(), 60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid Staff ID');
      setPiStaffValue('');
    } finally {
      setPiStaffValidating(false);
    }
  };
  const handleRemovePiStaff = (staff_id) => {
    setPiStaffList((prev) => prev.filter((s) => s.staff_id !== staff_id));
  };

  const closeReceipt = async (bundlesArg) => {
    const bundles = bundlesArg || scannedBundles;
    if (bundles.length === 0) return;
    setIsSubmitting(true);
    try {
      const docId = selectedDocument.transfer_id || selectedDocument.entry_id;
      await apiClient.post('/internal-transfer/complete-bundle-receipt', {
        transfer_id: docId,
        section_name: 'Inhouse',
        bundles: bundles.map(b => b.bundle_barcode),
        bundle_details: bundles.map(b => ({
          gate_bundle: b.gate_bundle,
          original_bundle: b.original_bundle,
          sort_no: b.sort_no,
          color: b.color,
          qty: b.qty,
          vendor_name: b.vendor_name,
        })),
      });
      toast.success(`All ${bundles.length} bundles received — receipt completed`);
      clearDraft();
      setSelectedDocument(null);
      setScannedBundles([]);
      setResumedDraft(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to complete receipt'); }
    finally { setIsSubmitting(false); fetchDocuments(); }
  };

  if (isLoading) {
    return <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" /></div>;
  }

  const totalDocs = documents.length;
  const totalDocPages = Math.max(1, Math.ceil(totalDocs / DOC_PAGE_SIZE));
  const safeDocPage = Math.min(docPage, totalDocPages);
  const pagedDocuments = documents.slice((safeDocPage - 1) * DOC_PAGE_SIZE, safeDocPage * DOC_PAGE_SIZE);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="inhouse-bundle-receipt-page">
      <header className="bg-[#1A1F2E] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/inhouse')} className="text-white hover:bg-white/10">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Bundle Receipt Entry</h1>
              <p className="text-sm text-gray-400">Inhouse Section</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DraftsMenu
              drafts={drafts}
              onResume={handleResumeDraft}
              onDelete={removeDraft}
              activeDraftId={activeDraftId}
              saving={saving}
              lastSavedAt={lastSavedAt}
              variant="dark"
            />
            <Button variant="outline" size="sm" onClick={() => fetchDocuments()} data-testid="inhouse-doc-refresh" className="text-white border-white/30 hover:bg-white/10">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditQtyOpen(true)}
                className="ml-2 bg-amber-500/20 border-amber-400/30 text-amber-100 hover:bg-amber-500/30"
                data-testid="admin-edit-qty-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Admin: Edit Bundle Qty
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {resumedDraft && selectedDocument && (
          <ResumedDraftBanner draft={resumedDraft} onStartFresh={handleStartFresh} />
        )}
        {/* My Recent Receipts — edit wrongly-entered original bundle numbers
            from the last 7 days. Receipts that have moved to Qty Confirmation /
            Folding / Pressing are locked and cannot be edited from here. */}
        {!selectedDocument && (
          <Card className="border-sky-200" data-testid="recent-receipts-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sky-700 text-base">
                <Printer className="w-5 h-5" /> My Recent Receipts
                <Badge className="bg-sky-100 text-sky-800 border-0" data-testid="recent-count-badge">
                  {recentReceipts.length} receipt(s)
                </Badge>
              </CardTitle>
              <CardDescription>
                Correct or discard recent entries. Receipts whose USNs have moved to the next stage are locked.
                {' '}<span className="text-amber-700">Max range: 7 days.</span>
              </CardDescription>
              <div className="flex flex-wrap items-end gap-2 pt-2">
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500" htmlFor="recent-from">From</label>
                  <Input
                    id="recent-from"
                    type="date"
                    value={recentFromDate}
                    max={recentToDate}
                    onChange={(e) => setRecentFromDate(e.target.value)}
                    className="h-8 w-[140px] text-xs"
                    data-testid="recent-from-date"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[10px] uppercase tracking-wide text-slate-500" htmlFor="recent-to">To</label>
                  <Input
                    id="recent-to"
                    type="date"
                    value={recentToDate}
                    min={recentFromDate}
                    max={_todayStr()}
                    onChange={(e) => setRecentToDate(e.target.value)}
                    className="h-8 w-[140px] text-xs"
                    data-testid="recent-to-date"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs"
                  onClick={() => fetchRecentReceipts()}
                  disabled={recentLoading}
                  data-testid="recent-apply-btn"
                >
                  {recentLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                  Apply
                </Button>
                <div className="flex gap-1 text-[11px]">
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                    onClick={() => {
                      const t = _todayStr();
                      setRecentFromDate(t);
                      setRecentToDate(t);
                      fetchRecentReceipts(t, t);
                    }}
                    data-testid="recent-preset-today"
                  >Today</button>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                    onClick={() => {
                      const f = _daysAgoStr(2);
                      const t = _todayStr();
                      setRecentFromDate(f);
                      setRecentToDate(t);
                      fetchRecentReceipts(f, t);
                    }}
                    data-testid="recent-preset-3d"
                  >Last 3 days</button>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-slate-200 hover:bg-slate-50"
                    onClick={() => {
                      const f = _daysAgoStr(6);
                      const t = _todayStr();
                      setRecentFromDate(f);
                      setRecentToDate(t);
                      fetchRecentReceipts(f, t);
                    }}
                    data-testid="recent-preset-7d"
                  >Last 7 days</button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {recentReceipts.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-500" data-testid="recent-empty-state">
                  {recentLoading ? 'Loading…' : 'No receipts in the selected range.'}
                </div>
              ) : (
              <div className="max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Gate / Receipt</TableHead>
                      <TableHead className="text-xs">Original</TableHead>
                      <TableHead className="text-xs">Sort / Design</TableHead>
                      <TableHead className="text-xs">Qty</TableHead>
                      <TableHead className="text-xs">Flow</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentReceipts.map((r) => (
                      <TableRow key={r.bundle_code} data-testid={`recent-row-${r.bundle_code}`}>
                        <TableCell className="text-xs font-mono">{r.gate_bundle || r.bundle_code || '-'}</TableCell>
                        <TableCell className="text-xs font-mono">{r.original_bundle || '-'}</TableCell>
                        <TableCell className="text-xs">
                          <div>{r.sort_no || '-'}</div>
                          <div className="text-[10px] text-slate-500">{r.design_name || ''}</div>
                        </TableCell>
                        <TableCell className="text-xs font-semibold">
                          {r.flow_type === 'RUNNING_MTR'
                            ? `${r.received_mtr ?? 0} Mtr`
                            : (r.usn_count ?? 0)}
                        </TableCell>
                        <TableCell className="text-xs">
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              r.flow_type === 'RUNNING_MTR' ? 'border-purple-300 text-purple-700' :
                              r.flow_type === 'THAN_PROCESSING' ? 'border-emerald-300 text-emerald-700' :
                              r.flow_type === 'SHIRTING' ? 'border-amber-300 text-amber-700' :
                              r.flow_type === 'FLEXI' ? 'border-blue-300 text-blue-700' :
                              'border-slate-300 text-slate-600'
                            }`}
                            data-testid={`recent-flow-${r.bundle_code}`}
                          >
                            {r.flow_type || 'REGULAR'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.editable ? (
                            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">Editable</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-500">
                              Locked · {r.locked_by || 'downstream'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px]"
                              disabled={!r.editable}
                              onClick={() => openEditOriginalDialog(r)}
                              data-testid={`recent-edit-${r.bundle_code}`}
                            >
                              Edit Original
                            </Button>
                            {canPickFlowType && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                                disabled={!(r.flow_editable ?? r.editable)}
                                title={r.flow_editable === false && r.editable ? 'Processing Inward already done — flow type is locked' : undefined}
                                onClick={() => openEditFlowDialog(r)}
                                data-testid={`recent-edit-flow-${r.bundle_code}`}
                              >
                                Edit Flow
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-[11px] border-red-300 text-red-700 hover:bg-red-50"
                              disabled={!r.editable || discardingCode === r.bundle_code}
                              onClick={() => handleDiscardReceipt(r)}
                              data-testid={`recent-discard-${r.bundle_code}`}
                              title="Discard this incomplete draft — releases USNs and re-opens the IT"
                            >
                              {discardingCode === r.bundle_code
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <><X className="w-3 h-3 mr-1" /> Discard</>}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Multi-IT jobworker truck flow: scan original bundle codes;
            backend resolves to the matching open Processing IT and marks
            it received in that IT. */}
        {!selectedDocument && (
          <Card className="border-indigo-200" data-testid="proc-multi-it-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-indigo-700">
                <Truck className="w-5 h-5" /> Pending Processing Transfers
                <Badge className="bg-indigo-100 text-indigo-800 border-0">{procTransfers.length}</Badge>
              </CardTitle>
              <CardDescription>
                Bundles from a multi-IT jobworker truck — scan the original bundle code
                and the system will mark it received against the right Processing IT.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input
                  ref={procScanInputRef}
                  value={procScanInput}
                  onChange={(e) => setProcScanInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleScanProcBundle()}
                  placeholder="Scan original bundle code…"
                  className="font-mono"
                  data-testid="proc-multi-it-scan-input"
                  disabled={procScanning || procTransfers.length === 0}
                />
                <Button
                  onClick={handleScanProcBundle}
                  disabled={procScanning || !procScanInput.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="proc-multi-it-scan-btn"
                >
                  {procScanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Scan className="w-4 h-4 mr-1" /> Scan</>}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={fetchProcTransfers}
                  data-testid="proc-multi-it-refresh"
                  title="Refresh"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {procTransfers.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-4">
                  No pending Processing transfers awaiting bundle receipt.
                </p>
              ) : (
                <div className="space-y-2">
                  {procTransfers.map((it) => {
                    const isOpen = procExpandedIds.has(it.transfer_id);
                    const pct = Math.round((it.received_count / Math.max(it.total_bundles, 1)) * 100);
                    return (
                      <div
                        key={it.transfer_id}
                        className="border rounded-lg bg-white"
                        data-testid={`proc-it-card-${it.transfer_id}`}
                      >
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-stone-50"
                          onClick={() => toggleProcExpanded(it.transfer_id)}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-sm">{it.transfer_id}</span>
                              <Badge className="bg-stone-100 text-stone-800 border-0 text-[10px]">
                                {it.processing_unit_name || it.from_section}
                              </Badge>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              Shipped: {it.shipped_at?.slice(0, 19).replace('T', ' ') || '—'}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-semibold text-emerald-600">
                              {it.received_count}/{it.total_bundles}
                            </div>
                            <div className="w-24 h-1.5 bg-stone-200 rounded mt-1">
                              <div
                                className="h-1.5 bg-emerald-500 rounded"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        {isOpen && (
                          <div className="border-t bg-stone-50 p-3">
                            <div className="text-xs font-medium mb-2 text-stone-600">
                              {it.remaining_count} bundle(s) still to receive:
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                              {it.unreceived_bundles.map((b) => (
                                <div
                                  key={b.bundle_code}
                                  className="bg-white border border-stone-200 rounded px-2 py-1 text-[11px] font-mono"
                                  data-testid={`proc-unreceived-${b.bundle_code}`}
                                >
                                  {b.bundle_code}
                                  {b.design_name && (
                                    <div className="text-[10px] text-stone-500 font-sans">
                                      {b.design_name}{b.shade ? ` · ${b.shade}` : ''}{b.size ? ` · ${b.size}` : ''}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Document Selection */}
        {!selectedDocument ? (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Truck className="w-5 h-5" /> Select Document</CardTitle>
              <CardDescription>Choose a document to process bundle receipts. Can't find an older one? Search by DOC / gate number.</CardDescription>
              <div className="flex items-center gap-2 pt-2">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    ref={docSearchInputRef}
                    value={docSearchInput}
                    onChange={(e) => handleDocSearchChange(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDocSearchSubmit()}
                    placeholder="Search DOC / gate / transfer number…"
                    className="pl-8 h-9"
                    data-testid="inhouse-doc-search"
                  />
                </div>
                {docSearching && <Loader2 className="w-4 h-4 animate-spin text-gray-400" data-testid="inhouse-doc-searching" />}
                {docSearchInput && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 text-xs"
                    onClick={() => { setDocSearchInput(''); fetchDocuments('', { silent: true }); }}
                    data-testid="inhouse-doc-search-clear"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-center text-gray-500 py-8" data-testid="inhouse-doc-empty">
                  {docSearchInput ? `No documents match "${docSearchInput}"` : 'No pending documents for Inhouse section'}
                </p>
              ) : (
                <div className="space-y-2">
                  {pagedDocuments.map(doc => {
                    const docId = doc.transfer_id || doc.entry_id;
                    const bundleCount = (doc.bundles || doc.bundle_codes || []).length;
                    const hasDraft = (drafts || []).some(d => d.document_id === docId);
                    return (
                      <div key={docId} className="flex items-center justify-between border rounded-lg p-3 hover:bg-gray-50 cursor-pointer" onClick={() => handleSelectDocument(docId)} data-testid={`inhouse-doc-${docId}`}>
                        <div>
                          <span className="font-mono font-bold text-sm">{docId}</span>
                          <span className="text-xs text-gray-500 ml-3">{doc.from_section || doc.vendor_name || ''}</span>
                          {bundleCount > 0 && <Badge variant="outline" className="ml-2">{bundleCount} bundles</Badge>}
                        </div>
                        <div className="flex items-center gap-2">
                          {hasDraft && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`inhouse-doc-draft-${docId}`}>
                              Draft in progress
                            </Badge>
                          )}
                          {(() => {
                            const rp = doc.receipt_progress;
                            if (rp && rp.expected > 0) {
                              if (rp.complete) {
                                return <Badge className="bg-green-100 text-green-800" data-testid={`inhouse-doc-status-${docId}`}>All {rp.expected} received</Badge>;
                              }
                              return <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`inhouse-doc-status-${docId}`}>{rp.received}/{rp.expected} received · {rp.pending} pending</Badge>;
                            }
                            return <Badge className={
                              doc.status === 'received' ? 'bg-green-100 text-green-800' :
                              doc.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                              doc.status === 'requested' ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            } data-testid={`inhouse-doc-status-${docId}`}>{doc.status}</Badge>;
                          })()}
                        </div>
                      </div>
                    );
                  })}
                  {totalDocPages > 1 && (
                    <div className="flex items-center justify-between pt-2" data-testid="inhouse-doc-pagination">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safeDocPage <= 1}
                        onClick={() => setDocPage(p => Math.max(1, p - 1))}
                        data-testid="inhouse-doc-prev"
                      >
                        Previous
                      </Button>
                      <span className="text-xs text-gray-500" data-testid="inhouse-doc-page-info">
                        Page {safeDocPage} of {totalDocPages} · {totalDocs} documents
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={safeDocPage >= totalDocPages}
                        onClick={() => setDocPage(p => Math.min(totalDocPages, p + 1))}
                        data-testid="inhouse-doc-next"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Selected doc header */}
            <div className="flex items-center justify-between bg-white rounded-lg border p-3">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-teal-600" />
                <span className="font-mono font-bold">{selectedDocument.transfer_id || selectedDocument.entry_id}</span>
                <span className="text-sm text-gray-500">{selectedDocument.from_section || ''}</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setSelectedDocument(null); setScannedBundles([]); setResumedDraft(null); }}>Change Document</Button>
            </div>

            {/* Phase 2 merge — working staff for auto Processing Inward */}
            <Card className="border-cyan-200" data-testid="pi-staff-card">
              <CardContent className="pt-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan-600" />
                  <span className="text-sm font-medium text-gray-800">Working Staff — auto Processing Inward</span>
                  <Badge className="bg-cyan-100 text-cyan-800 border-0" data-testid="pi-staff-count">{piStaffList.length} staff</Badge>
                </div>
                <p className="text-xs text-gray-500">
                  Each bundle is auto-received into Processing Inward right after print. Received qty is split equally across these staff.
                </p>
                <form onSubmit={handleAddPiStaff} className="flex gap-2">
                  <Input
                    ref={piStaffInputRef}
                    value={piStaffValue}
                    onChange={(e) => setPiStaffValue(e.target.value)}
                    placeholder="Scan / type Staff ID"
                    className="max-w-xs"
                    data-testid="pi-staff-input"
                  />
                  <Button type="submit" disabled={piStaffValidating || !piStaffValue.trim()} data-testid="pi-staff-add-btn">
                    {piStaffValidating ? '...' : (<><Plus className="w-4 h-4 mr-1" />Add</>)}
                  </Button>
                </form>
                {piStaffList.length > 0 ? (
                  <div className="flex flex-wrap gap-2" data-testid="pi-staff-chips">
                    {piStaffList.map((s) => (
                      <div key={s.staff_id} className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1" data-testid={`pi-staff-chip-${s.staff_id}`}>
                        <UserCheck className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-xs font-medium text-green-800">{s.staff_name}</span>
                        <span className="text-[10px] text-gray-500">{s.staff_id}</span>
                        <button type="button" onClick={() => handleRemovePiStaff(s.staff_id)} className="text-red-500 hover:text-red-700" data-testid={`pi-staff-remove-${s.staff_id}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="pi-staff-warning">
                    <AlertTriangle className="w-3 h-3" /> Add at least one staff to start scanning bundles.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Bundle scan input */}
            <Card>
              <CardContent className="pt-4">
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Scan Gate Entry Bundle Barcode</label>
                <div className="flex gap-2">
                  <Input ref={bundleInputRef} value={bundleInput} onChange={e => setBundleInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleScanGateBundle()}
                    placeholder={piStaffList.length === 0 ? 'Add staff first…' : 'Scan bundle barcode...'}
                    disabled={piStaffList.length === 0}
                    autoFocus data-testid="gate-bundle-input" />
                  <Button onClick={handleScanGateBundle} disabled={!bundleInput.trim() || piStaffList.length === 0}>
                    <Scan className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Scanned bundles table */}
            {scannedBundles.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Scanned Bundles ({scannedBundles.length})</span>
                    <Badge className="bg-teal-100 text-teal-800">{scannedBundles.reduce((s, b) => s + (b.qty || 0), 0)} total pcs</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Gate Bundle</TableHead>
                          <TableHead>Original Bundle</TableHead>
                          <TableHead>Sort No</TableHead>
                          <TableHead>Color</TableHead>
                          <TableHead className="text-center">Qty</TableHead>
                          <TableHead className="text-center">Received Qty</TableHead>
                          <TableHead>Flow</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Inward</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {scannedBundles.map(b => (
                          <TableRow key={b.gate_bundle}>
                            <TableCell className="font-mono text-xs">{b.gate_bundle}</TableCell>
                            <TableCell className="font-mono text-xs font-bold">{b.original_bundle}</TableCell>
                            <TableCell className="text-xs">{b.sort_no || '-'}</TableCell>
                            <TableCell className="text-xs">{b.color || '-'}</TableCell>
                            <TableCell className="text-center font-bold">
                              {b.flow_type === 'RUNNING_MTR'
                                ? `${b.received_mtr ?? 0} Mtr`
                                : b.qty}
                            </TableCell>
                            <TableCell className="text-center font-bold text-emerald-700" data-testid={`received-qty-${b.gate_bundle}`}>
                              {b.flow_type === 'RUNNING_MTR'
                                ? `${b.received_qty ?? 0} Mtr`
                                : (b.received_qty ?? '-')}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-[10px] ${
                                  b.flow_type === 'RUNNING_MTR' ? 'border-purple-300 text-purple-700' :
                                  b.flow_type === 'THAN_PROCESSING' ? 'border-emerald-300 text-emerald-700' :
                                  b.flow_type === 'SHIRTING' ? 'border-amber-300 text-amber-700' :
                                  b.flow_type === 'FLEXI' ? 'border-blue-300 text-blue-700' :
                                  'border-slate-300 text-slate-600'
                                }`}
                                data-testid={`scanned-flow-${b.gate_bundle}`}
                              >
                                {b.flow_type || 'REGULAR'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{b.vendor_name || '-'}</TableCell>
                            <TableCell>
                              {b.inward_status === 'failed' ? (
                                <Badge className="bg-red-100 text-red-700 border-0" data-testid={`inward-status-${b.gate_bundle}`}>Failed</Badge>
                              ) : b.inward_status ? (
                                <Badge className="bg-emerald-100 text-emerald-700 border-0" data-testid={`inward-status-${b.gate_bundle}`}>
                                  <Check className="w-3 h-3 mr-0.5" />{b.inward_status}
                                </Badge>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => setRemoveBundleTarget(b.gate_bundle)} data-testid={`remove-bundle-${b.gate_bundle}`}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {(() => {
                    const committed = scannedBundles.filter((b) => b.inward_status && b.inward_status !== 'failed');
                    const expectedTotal = selectedDocument?.bundles?.length
                      || selectedDocument?.bundle_codes?.length
                      || selectedDocument?.bundle_count
                      || 0;
                    const isComplete = expectedTotal > 0 && committed.length >= expectedTotal;
                    const remaining = Math.max(expectedTotal - committed.length, 0);
                    return (
                      <div
                        className="w-full mt-4 flex items-center justify-between rounded-lg border border-[#E5DFD0] bg-[#F5F1E8] px-4 py-3"
                        data-testid="receipt-progress-status"
                      >
                        <span className="text-sm font-medium text-[#1A1F2E]">
                          {committed.length}{expectedTotal > 0 ? ` / ${expectedTotal}` : ''} bundles received
                        </span>
                        <Badge className={isComplete ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                          {isSubmitting
                            ? 'Finalising…'
                            : (expectedTotal > 0
                                ? (isComplete ? 'Complete' : `Partially Received — ${remaining} remaining`)
                                : 'Auto-saved')}
                        </Badge>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Original Bundle Dialog */}
      <Dialog open={showOriginalDialog} onOpenChange={setShowOriginalDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-600" /> Original Bundle Number
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">Gate entry bundle: <strong>{currentGateBundle}</strong></DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Original bundle scan */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Scan / Enter Original Bundle Number</label>
              <div className="flex gap-2">
                <Input ref={originalInputRef} value={originalBundleCode} onChange={e => setOriginalBundleCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleLookupOriginal()}
                  placeholder="Original bundle barcode..." data-testid="original-bundle-input" />
                <Button onClick={handleLookupOriginal} disabled={lookingUp || !originalBundleCode.trim()} data-testid="lookup-original-btn">
                  {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {/* Live pool hint — informational only since 2026-05-04 rollback.
                  Legacy bundles are still flowing in and must be accepted. */}
              {originalBundleCode.trim() && validBundlePool.size > 0 && !validBundlePool.has(originalBundleCode.trim()) && (
                <div className="mt-1 text-xs text-slate-500 flex items-center gap-1" data-testid="original-bundle-not-in-pool">
                  <AlertTriangle className="w-3 h-3" />
                  Not in the current PROC→Inhouse pool — likely a legacy bundle. Proceeding is allowed.
                </div>
              )}
              {originalBundleCode.trim() && validBundlePool.has(originalBundleCode.trim()) && (
                <div className="mt-1 text-xs text-emerald-600 flex items-center gap-1" data-testid="original-bundle-in-pool">
                  <Check className="w-3 h-3" />
                  Bundle is in an open PROC→Inhouse IT.
                </div>
              )}
            </div>

            {/* Phase A — Inhouse 4-flow foundation. Supervisor/Incharge/Admin
                only; everyone else gets REGULAR (locked). */}
            {canPickFlowType && (
              <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-2" data-testid="flow-type-block">
                <label className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Flow Type
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-700">Supervisor</span>
                </label>
                <select
                  value={flowType}
                  onChange={(e) => setFlowType(e.target.value)}
                  className="w-full rounded-md border border-amber-300 bg-white text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="flow-type-select"
                >
                  {FLOW_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {flowType === 'JOB_WORKER' && (
                  <div data-testid="job-worker-block">
                    <label className="text-[11px] text-gray-600">Job Worker <span className="text-red-500">*</span></label>
                    <input
                      list="job-workers-list"
                      value={jobWorkerName}
                      onChange={(e) => setJobWorkerName(e.target.value)}
                      placeholder="Type to search job worker…"
                      className="w-full rounded-md border border-amber-300 bg-white text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                      data-testid="job-worker-input"
                    />
                    <datalist id="job-workers-list">
                      {jobWorkers.map((jw) => (
                        <option key={jw.vendor_name} value={jw.vendor_name} />
                      ))}
                    </datalist>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Sent out for folding &amp; pressing. Skips internal Folding &amp; Pressing.
                    </p>
                  </div>
                )}
                {flowType === 'SHIRTING' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-gray-600">Consumption / Pc (Mtr)</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={consumptionPerPc}
                        onChange={(e) => setConsumptionPerPc(e.target.value)}
                        placeholder="e.g., 1.60"
                        data-testid="flow-consumption-per-pc"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        Metres consumed for <span className="font-semibold">one</span> shirt (typically 1.0–3.0 Mtr).
                      </p>
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-600">Received Mtr</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={receivedMtr}
                        onChange={(e) => setReceivedMtr(e.target.value)}
                        placeholder="e.g., 160"
                        data-testid="flow-received-mtr"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        Total metres in the bundle.
                      </p>
                    </div>
                    {(() => {
                      const cpp = parseFloat(consumptionPerPc);
                      const rm = parseFloat(receivedMtr);
                      if (!cpp || !rm || cpp <= 0 || rm <= 0) return null;
                      const pcs = Math.floor(rm / cpp);
                      const looksWrong = pcs <= 1;
                      return (
                        <div
                          className={`col-span-2 mt-1 px-3 py-2 rounded border text-sm font-medium ${
                            looksWrong
                              ? 'bg-red-50 border-red-300 text-red-800'
                              : 'bg-emerald-50 border-emerald-300 text-emerald-800'
                          }`}
                          data-testid="shirting-pieces-preview"
                        >
                          {looksWrong ? (
                            <>
                              ⚠ This will create only <span className="font-bold">{pcs}</span> piece{pcs === 1 ? '' : 's'}.
                              Consumption/Pc ({cpp} Mtr) must be the metres for <em>one</em> piece,
                              not the total. Typical value: 1.0–3.0 Mtr.
                            </>
                          ) : (
                            <>
                              → {pcs} piece{pcs === 1 ? '' : 's'} will be created
                              ({rm} Mtr ÷ {cpp} Mtr/pc = {pcs}).
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
                {flowType === 'RUNNING_MTR' && (
                  <div>
                    <label className="text-[11px] text-gray-600">Received Mtr (single USN)</label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={receivedMtr}
                      onChange={(e) => setReceivedMtr(e.target.value)}
                      placeholder="e.g., 160"
                      data-testid="flow-received-mtr-running"
                    />
                  </div>
                )}
                <p className="text-[10.5px] text-amber-800/80 leading-relaxed">
                  <span className="font-semibold">Per-bundle:</span> the flow set here applies to the bundle currently being scanned. Change it before each scan to mix flows in one receipt (e.g., 50 Regular + 25 Flexi + 10 Shirting + 15 Running Mtr).
                </p>
                <p className="text-[10.5px] text-amber-800/80 leading-relaxed">
                  {flowType === 'REGULAR' && 'Standard pipeline: PI → Folding → (Damage|Pressing) → Assortment → IT.'}
                  {flowType === 'FLEXI' && 'Pressing will be skipped. PI → Folding → (Damage|skip-press) → Assortment → IT.'}
                  {flowType === 'SHIRTING' && 'Receipt qty (Mtr) will be converted to pieces using consumption/pc at PI.'}
                  {flowType === 'RUNNING_MTR' && 'Mints exactly 1 USN per bundle carrying the Mtr value. Folding/Pressing/Assortment skipped; IT is manual.'}
                  {flowType === 'THAN_PROCESSING' && 'Keeps normal pieces. Folding/Pressing/Damage/Assortment skipped — bundle is parked for a direct manual Internal Transfer.'}
                </p>
              </div>
            )}

            {/* Greige data found */}
            {greigData && !needsManual && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2" data-testid="greige-data-found">
                <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                  <Check className="w-4 h-4" /> Greige data found
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Sort No:</span> <span className="font-bold">{greigData.sort_no}</span></div>
                  <div><span className="text-gray-500">Color:</span> <span className="font-bold">{greigData.color || greigData.shade || '-'}</span></div>
                  <div><span className="text-gray-500">Qty:</span> <span className="font-bold">{greigData.sent_qty || greigData.usn_count || 0}</span></div>
                  <div><span className="text-gray-500">Vendor:</span> <span className="font-bold">{greigData.vendor_name || '-'}</span></div>
                  {greigData.design_name && <div><span className="text-gray-500">Design:</span> <span className="font-bold">{greigData.design_name}</span></div>}
                </div>
              </div>
            )}

            {/* Received Qty — combined Processing Inward step (piece flows only) */}
            {greigData && !needsManual &&
             !(canPickFlowType && (flowType === 'SHIRTING' || flowType === 'RUNNING_MTR')) && (
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3" data-testid="received-qty-block">
                <label className="text-sm font-medium text-teal-900 mb-1 flex items-center gap-1.5">
                  <PackageCheck className="w-4 h-4" /> Received Qty (Processing Inward) *
                </label>
                <Input
                  type="text"
                  inputMode="numeric"
                  value={receivedQty}
                  onChange={(e) => setReceivedQty(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder={`Expected ${greigData.sent_qty || greigData.usn_count || 0}`}
                  data-testid="received-qty-input"
                />
                <p className="text-[11px] text-teal-800/80 mt-1">
                  Actual pieces physically received. Pre-filled with the expected/sent qty —
                  change it if the count is short or in excess.
                </p>
              </div>
            )}

            {/* Manual entry */}
            {needsManual && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-3" data-testid="manual-entry-form">
                <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" /> No Greige data — enter manually
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Sort No *</label>
                    <div className="flex gap-1">
                      <Input value={manualSortNo} onChange={e => setManualSortNo(e.target.value)} placeholder="Sort No" data-testid="manual-sort-no" />
                      <Button variant="outline" size="sm" onClick={handleSortNoLookup} disabled={lookingUpSort || !manualSortNo.trim()} className="shrink-0">
                        {lookingUpSort ? '...' : 'Fetch'}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Vendor</label>
                    <Input value={manualVendor} readOnly className="bg-gray-50" data-testid="manual-vendor" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Color *</label>
                    <div className="relative">
                      <Input
                        value={colorSearch || manualColor}
                        onChange={e => { setColorSearch(e.target.value); setManualColor(''); setShowColorDropdown(true); }}
                        onFocus={() => setShowColorDropdown(true)}
                        placeholder="Search color..."
                        data-testid="manual-color"
                      />
                      {showColorDropdown && (
                        <div className="absolute z-50 w-full mt-1 max-h-52 overflow-y-auto bg-white border rounded-lg shadow-lg">
                          {colorOptions
                            .filter(c => c.color_name.toLowerCase().includes((colorSearch || '').toLowerCase()))
                            .map(c => (
                              <div key={c.color_code} className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex items-center gap-3"
                                onClick={() => { setManualColor(c.color_name); setColorSearch(c.color_name); setShowColorDropdown(false); }}>
                                <div className="w-8 h-8 rounded border border-gray-300 shrink-0"
                                  style={{ backgroundColor: `rgb(${c.red_code}, ${c.green_code}, ${c.blue_code})` }} />
                                <div>
                                  <p className="text-sm font-medium">{c.color_name}</p>
                                  <p className="text-xs text-gray-400">({c.red_code}-{c.green_code}-{c.blue_code})</p>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-700 mb-1 block">
                      Qty {canPickFlowType && (flowType === 'SHIRTING' || flowType === 'RUNNING_MTR') ? '' : '*'}
                    </label>
                    {canPickFlowType && flowType === 'SHIRTING' ? (
                      <Input
                        type="text"
                        value={(() => {
                          const cpp = parseFloat(consumptionPerPc);
                          const rm = parseFloat(receivedMtr);
                          if (!(cpp > 0) || !(rm > 0)) return '—';
                          const pcs = Math.floor(rm / cpp);
                          return pcs > 1 ? `${pcs} (auto)` : `${pcs} ⚠`;
                        })()}
                        readOnly
                        className="bg-gray-50 text-gray-700"
                        data-testid="manual-qty-shirting-auto"
                      />
                    ) : canPickFlowType && flowType === 'RUNNING_MTR' ? (
                      <Input
                        type="text"
                        value="1 USN (auto)"
                        readOnly
                        className="bg-gray-50 text-gray-700"
                        data-testid="manual-qty-running-mtr-auto"
                      />
                    ) : (
                      <Input type="number" min="1" value={manualQty} onChange={e => setManualQty(e.target.value)} placeholder="0" data-testid="manual-qty" />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOriginalDialog(false)}>Cancel</Button>
            <Button onClick={handleConfirmOriginal} disabled={!originalBundleCode.trim()} data-testid="confirm-original-btn">
              <Printer className="w-4 h-4 mr-2" /> Confirm & Print Sticker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Dialog — 2 copies */}
      <PrintDialog open={showPrintDialog} onOpenChange={handlePrintClose} codes={printCodes} extraTexts={printExtraTexts} labelType="bundle" operationKey="inhouse-bundle-receipt" title="Print Bundle Sticker (2 copies)" />

      {/* Admin: Edit Saved Bundle Qty */}
      <Dialog open={editQtyOpen} onOpenChange={setEditQtyOpen}>
        <DialogContent data-testid="edit-qty-dialog">
          <DialogHeader>
            <DialogTitle>Edit Saved Bundle Qty (Admin)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 text-xs">
              Increasing qty claims fresh PI USNs. Decreasing releases trailing USNs back to the pool —
              blocked if any of them already moved downstream (folded, pressed, packed, etc.).
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Bundle Code *</label>
              <div className="flex gap-2">
                <Input
                  value={editBundleCode}
                  onChange={(e) => setEditBundleCode(e.target.value.toUpperCase())}
                  placeholder="BUN0XXXXX/YY-YY"
                  className="font-mono"
                  data-testid="edit-qty-bundle-input"
                />
                <Button variant="outline" onClick={handleEditLookup} disabled={editLoading || !editBundleCode.trim()}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {editPreview && (
              <div className="bg-gray-50 border rounded p-3 text-xs space-y-1">
                <p><span className="text-gray-500">Current Qty:</span> <span className="font-bold">{editPreview.current_qty}</span></p>
                <p><span className="text-gray-500">Sort No:</span> {editPreview.sort_no || '-'} &nbsp; <span className="text-gray-500">Shade:</span> {editPreview.shade || '-'}</p>
                <p><span className="text-gray-500">Design:</span> {editPreview.design_name || '-'}</p>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">New Qty *</label>
              <Input
                type="number" min="0"
                value={editNewQty}
                onChange={(e) => setEditNewQty(e.target.value)}
                placeholder="0"
                data-testid="edit-qty-new-qty"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1 block">Reason</label>
              <Input
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Audit note (optional)"
                data-testid="edit-qty-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditQtyOpen(false)}>Cancel</Button>
            <Button
              onClick={handleEditQtySubmit}
              disabled={editLoading || !editBundleCode.trim() || editNewQty === ''}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="edit-qty-confirm-btn"
            >
              {editLoading ? 'Updating…' : 'Apply Qty Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Original Bundle Number — corrects a wrongly-entered bundle
          on an existing receipt. Validates new value against the live
          PROC→Inhouse IT bundle pool before submit. */}
      <Dialog open={editOrigOpen} onOpenChange={setEditOrigOpen}>
        <DialogContent data-testid="edit-orig-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Original Bundle Number</DialogTitle>
          </DialogHeader>
          {editOrigRow && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-slate-50 rounded border space-y-1">
                <div><span className="text-slate-500">Receipt:</span> <span className="font-mono font-semibold">{editOrigRow.bundle_code}</span></div>
                <div><span className="text-slate-500">Current Original:</span> <span className="font-mono text-red-600">{editOrigRow.original_bundle || '-'}</span></div>
                <div><span className="text-slate-500">Current Sort/Design:</span> <span className="font-medium">{editOrigRow.sort_no || '-'} · {editOrigRow.design_name || '-'}</span></div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">New Original Bundle Number *</label>
                <Input
                  autoFocus
                  value={editOrigNewValue}
                  onChange={e => setEditOrigNewValue(e.target.value)}
                  placeholder="e.g. BUN058766/26-27"
                  data-testid="edit-orig-new-value"
                  className="font-mono"
                />
                {editOrigNewValue.trim() && validBundlePool.size > 0 && !validBundlePool.has(editOrigNewValue.trim()) && (
                  <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Not in current PROC pool — likely a legacy bundle. Proceeding is allowed.
                  </div>
                )}
                {editOrigNewValue.trim() && validBundlePool.has(editOrigNewValue.trim()) && (
                  <div className="mt-1 text-[11px] text-emerald-600 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Valid PROC bundle.
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Reason</label>
                <Input
                  value={editOrigReason}
                  onChange={e => setEditOrigReason(e.target.value)}
                  placeholder="typo_correction / wrong-bundle / ..."
                  data-testid="edit-orig-reason"
                />
              </div>
              <div className="text-[11px] text-slate-500 p-2 bg-amber-50 border border-amber-100 rounded">
                On save: Greige details will be re-pulled, the old PROC IT received_bundles will be updated, and the change will be logged for audit.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrigOpen(false)} disabled={editOrigBusy}>Cancel</Button>
            <Button
              onClick={handleSubmitOriginalEdit}
              disabled={editOrigBusy || !editOrigNewValue.trim()}
              data-testid="edit-orig-submit-btn"
            >
              {editOrigBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Correction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Flow Type — corrects a bundle's flow_type before Folding starts.
          Only available to Supervisor / Incharge / Admin. */}
      <Dialog open={editFlowOpen} onOpenChange={setEditFlowOpen}>
        <DialogContent data-testid="edit-flow-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Flow Type</DialogTitle>
          </DialogHeader>
          {editFlowRow && (
            <div className="space-y-3 text-sm">
              <div className="p-3 bg-slate-50 rounded border space-y-1">
                <div><span className="text-slate-500">Bundle:</span> <span className="font-mono font-semibold">{editFlowRow.bundle_code}</span></div>
                <div><span className="text-slate-500">Current Flow:</span> <span className="font-semibold">{editFlowRow.flow_type || 'REGULAR'}</span></div>
                <div><span className="text-slate-500">Sort / Design:</span> <span className="font-medium">{editFlowRow.sort_no || '-'} · {editFlowRow.design_name || '-'}</span></div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">New Flow Type *</label>
                <select
                  value={editFlowNewType}
                  onChange={(e) => setEditFlowNewType(e.target.value)}
                  className="w-full rounded-md border border-slate-300 bg-white text-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="edit-flow-select"
                >
                  {FLOW_TYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              {editFlowNewType === 'SHIRTING' && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-gray-600">Consumption / Pc (Mtr)</label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={editFlowConsumption}
                      onChange={(e) => setEditFlowConsumption(e.target.value)}
                      placeholder="e.g., 1.60"
                      data-testid="edit-flow-cpp"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-600">Received Mtr</label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={editFlowReceivedMtr}
                      onChange={(e) => setEditFlowReceivedMtr(e.target.value)}
                      placeholder="e.g., 160"
                      data-testid="edit-flow-mtr"
                    />
                  </div>
                </div>
              )}
              {editFlowNewType === 'RUNNING_MTR' && (
                <div>
                  <label className="text-[11px] text-gray-600">Received Mtr (single USN)</label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={editFlowReceivedMtr}
                    onChange={(e) => setEditFlowReceivedMtr(e.target.value)}
                    placeholder="e.g., 80"
                    data-testid="edit-flow-mtr-running"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Reason</label>
                <Input
                  value={editFlowReason}
                  onChange={(e) => setEditFlowReason(e.target.value)}
                  placeholder="supervisor_correction"
                  data-testid="edit-flow-reason"
                />
              </div>
              <div className="text-[11px] text-slate-500 p-2 bg-amber-50 border border-amber-100 rounded">
                Allowed only before Folding / Pressing / Qty-Confirmation. Once any USN of this bundle moves downstream the flow is locked.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFlowOpen(false)} disabled={editFlowBusy}>Cancel</Button>
            <Button
              onClick={handleSubmitFlowEdit}
              disabled={editFlowBusy || !editFlowRow}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="edit-flow-submit-btn"
            >
              {editFlowBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Flow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmRemoveBundleDialog
        barcode={removeBundleTarget}
        onConfirm={() => { handleRemoveBundle(removeBundleTarget); setRemoveBundleTarget(null); }}
        onCancel={() => setRemoveBundleTarget(null)}
      />
      <ReturnCheckDialog data={returnCheck} onClose={() => setReturnCheck(null)} onApproved={() => { setReturnCheck(null); handleConfirmOriginal(); }} />
    </div>
  );
};

export default InhouseBundleReceiptEntry;
